import { create } from 'zustand'
import type { LiveChunk, LiveTool, Stroke } from '../services/annotation-geometry'

/**
 * Annotation strokes on shared documents, per document.
 *
 * Keyed by the presentation's id, so two documents on the stage keep their
 * own ink and switching between them does not carry one's drawings onto the
 * other. Within a document a stroke carries its own page.
 *
 * The room owns the strokes (RoomCoordinator); this is every client's mirror
 * of them — filled by `annotation:get` on catch-up, kept current by the
 * `annotation:*` events (see useAnnotations).
 *
 * Undo and redo are per person: undo takes back the last stroke YOU drew, never
 * someone else's — in a room of people drawing at once, "the last stroke" is
 * whoever happened to lift their pen last.
 */

/** A mark someone else is still making — or their laser — and when it last moved. */
export type LiveStroke = Omit<Stroke, 'tool'> & { tool: LiveTool; at: number }

/** How long an unfinished mark outlives its last update: its author left mid-line. */
export const LIVE_EXPIRY_MS = 3_000
/** A laser is gone the moment it stops moving, as a real one would be. */
export const LASER_EXPIRY_MS = 700
/** How much of a laser's path stays on screen as its trail. */
const LASER_TRAIL_POINTS = 24

type MeetingAnnotationState = {
  /** Document key → its strokes, oldest first (later strokes paint on top). */
  strokes: Record<string, Stroke[]>
  /** Document key → strokes this client undid, newest last: what redo restores. */
  undone: Record<string, Stroke[]>
  /** Document key → stroke id → a stroke someone else has not finished yet. */
  live: Record<string, Record<string, LiveStroke>>
  /** Add a stroke drawn HERE. Drawing again forgets what could be redone. */
  add: (doc: string, stroke: Stroke) => void
  /** Add a stroke someone else drew. Leaves this client's redo alone. */
  receive: (doc: string, stroke: Stroke) => void
  /** Replace a document's strokes with the room's — a catch-up. */
  replaceAll: (doc: string, strokes: Stroke[]) => void
  /** Remove strokes by id — the eraser, here or anyone's. */
  remove: (doc: string, ids: string[]) => void
  /** Take back `by`'s most recent stroke. */
  undo: (doc: string, by: string) => void
  /** Put back the stroke undone most recently. */
  redo: (doc: string) => void
  /** Remove `by`'s strokes, or everyone's when `by` is omitted. */
  clear: (doc: string, by?: string) => void
  /** Grow, or start, a stroke someone else is drawing. */
  liveAppend: (doc: string, by: string, chunk: LiveChunk, now: number) => void
  /** Forget an unfinished stroke — it was finished, or its author vanished. */
  liveDrop: (doc: string, id: string) => void
  /** Forget unfinished marks (and lasers) that stopped moving too long before `now`. */
  liveExpire: (now: number) => void
}

const without = <V,>(map: Record<string, V>, key: string): Record<string, V> => {
  const next = { ...map }
  delete next[key]
  return next
}

/** `list` with `stroke` on top, replacing any stroke with the same id. */
const upsert = (list: Stroke[] | undefined, stroke: Stroke): Stroke[] => [
  ...(list ?? []).filter((st) => st.id !== stroke.id),
  stroke,
]

export const useMeetingAnnotationStore = create<MeetingAnnotationState>((set) => ({
  strokes: {},
  undone: {},
  live: {},

  add: (doc, stroke) =>
    set((s) => ({
      strokes: { ...s.strokes, [doc]: upsert(s.strokes[doc], stroke) },
      undone: without(s.undone, doc),
    })),

  receive: (doc, stroke) =>
    set((s) => ({ strokes: { ...s.strokes, [doc]: upsert(s.strokes[doc], stroke) } })),

  replaceAll: (doc, strokes) =>
    set((s) => ({ strokes: { ...s.strokes, [doc]: strokes }, undone: without(s.undone, doc) })),

  remove: (doc, ids) =>
    set((s) => {
      const list = s.strokes[doc]
      if (!list || ids.length === 0) return s
      const drop = new Set(ids)
      return { strokes: { ...s.strokes, [doc]: list.filter((st) => !drop.has(st.id)) } }
    }),

  undo: (doc, by) =>
    set((s) => {
      const list = s.strokes[doc] ?? []
      // Not findLastIndex: the build targets es2017 and does not polyfill it.
      let i = list.length - 1
      while (i >= 0 && list[i].by !== by) i--
      if (i < 0) return s
      return {
        strokes: { ...s.strokes, [doc]: [...list.slice(0, i), ...list.slice(i + 1)] },
        undone: { ...s.undone, [doc]: [...(s.undone[doc] ?? []), list[i]] },
      }
    }),

  redo: (doc) =>
    set((s) => {
      const stack = s.undone[doc] ?? []
      const last = stack[stack.length - 1]
      if (!last) return s
      return {
        strokes: { ...s.strokes, [doc]: upsert(s.strokes[doc], last) },
        undone: { ...s.undone, [doc]: stack.slice(0, -1) },
      }
    }),

  clear: (doc, by) =>
    set((s) => ({
      strokes: by === undefined
        ? without(s.strokes, doc)
        : { ...s.strokes, [doc]: (s.strokes[doc] ?? []).filter((st) => st.by !== by) },
      undone: without(s.undone, doc),
    })),

  liveAppend: (doc, by, chunk, now) =>
    set((s) => {
      const strokes = s.live[doc] ?? {}
      const have = strokes[chunk.id]
      let points: number[]
      // A laser keeps only its recent path — its trail — and a gap does not
      // matter: it is a pointer, not a line anyone needs complete.
      if (chunk.tool === 'laser') points = [...(have?.points ?? []), ...chunk.points].slice(-LASER_TRAIL_POINTS * 2)
      // From the start: a new stroke, or a shape redrawn whole as it is dragged.
      else if (chunk.from === 0) points = chunk.points
      // The next slice of a stroke we have from its start. A slice that does
      // not join on — one was lost, or we joined mid-stroke — is skipped; the
      // finished stroke replaces the draft anyway.
      else if (have && have.points.length / 2 === chunk.from) points = [...have.points, ...chunk.points]
      else return s
      const next: LiveStroke = {
        id: chunk.id,
        by,
        page: chunk.page,
        tool: chunk.tool,
        color: chunk.color,
        width: chunk.width,
        points,
        at: now,
      }
      return { live: { ...s.live, [doc]: { ...strokes, [chunk.id]: next } } }
    }),

  liveDrop: (doc, id) =>
    set((s) => {
      const strokes = s.live[doc]
      if (!strokes?.[id]) return s
      return { live: { ...s.live, [doc]: without(strokes, id) } }
    }),

  liveExpire: (now) =>
    set((s) => {
      let changed = false
      const live: Record<string, Record<string, LiveStroke>> = {}
      for (const [doc, strokes] of Object.entries(s.live)) {
        const kept: Record<string, LiveStroke> = {}
        for (const [id, st] of Object.entries(strokes)) {
          if (now - st.at < (st.tool === 'laser' ? LASER_EXPIRY_MS : LIVE_EXPIRY_MS)) kept[id] = st
          else changed = true
        }
        live[doc] = kept
      }
      return changed ? { live } : s
    }),
}))
